import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {NgForm} from '@angular/forms';
import {Router, ActivatedRoute} from '@angular/router';
import {filter} from 'rxjs/operators';

import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {CoreActionCreatorService} from '@ispm/core/services/core-action-creator.service';
import {DashboardService} from '../../../../worklist/services/dashboard.service';
import {ExtendedHttpResponse} from '@ispm/core/services/http-interceptor.service';
import {IEService} from '@ispm/shared/services/IE.service';
import {IUserLogin} from '@ispm/migration/interfaces/user-login.interface';
import {LoginAuthService} from '../../../../authorization/services/login-auth.service';
import {NotificationMessageService} from '@ispm/core/components/customise-notification/notifications-message.service';
import {PdfService} from '@ispm/test/services/pdf.service';
import {UserActionCreatorService} from '@ispm/user/services/user-action-creator.service';
import {WorkflowService} from '@ispm/workflow/services/workflow.service';
import {userInfo, validationErrors} from '@ispm/migration/components/login-component/component/login-const';

@Component({
  selector: 'gen-app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {

  public formErrors: any = {
    loginId: '',
    password: ''
  };
  public forTestingPurposesOnly: boolean;
  public forTestingPurposesOnlyMessage: string;
  public loading = false;
  public loginError: boolean;
  public loginErrorMessage = '';
  public loginErrorObject: any;
  public user: IUserLogin;
  public version: string;

  @ViewChild('loginForm', { static: false }) public loginForm: NgForm;

  COPYRIGHT_CONTENT: string;
  FORGOT_PASSWORD_TITLE: string;
  LOGIN_APP_TITLE: string;
  LOGIN_BUTTON_TITLE: string;
  LOGIN_INVALID_CREDENTIALS: string;
  LOGIN_NAME_TITLE: string;
  LOGIN_PASSWORD_TITLE: string;
  PRIVACY_NOTES_TITLE: string;
  forgottenPasswordUrl: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private coreActionCreatorService: CoreActionCreatorService,
    private dashboardService: DashboardService,
    private ieService: IEService,
    private loginAuthService: LoginAuthService,
    private notificationMessageService: NotificationMessageService,
    private pdfService: PdfService,
    private route: Router,
    private userActionCreatorService: UserActionCreatorService,
    private workflowService: WorkflowService,
    private cd: ChangeDetectorRef
  ) {
    this.LOGIN_APP_TITLE = (<any> window).prefetchedTranslationData.LOGIN_APP_TITLE;
    this.COPYRIGHT_CONTENT = (<any> window).prefetchedTranslationData.COPYRIGHT_CONTENT;
    this.FORGOT_PASSWORD_TITLE = (<any> window).prefetchedTranslationData.FORGOT_PASSWORD_TITLE;
    this.PRIVACY_NOTES_TITLE = (<any> window).prefetchedTranslationData.PRIVACY_NOTES_TITLE;
    this.LOGIN_PASSWORD_TITLE = (<any> window).prefetchedTranslationData.LOGIN_PASSWORD_TITLE;
    this.LOGIN_BUTTON_TITLE = (<any> window).prefetchedTranslationData.LOGIN_BUTTON_TITLE;
    this.LOGIN_INVALID_CREDENTIALS = (<any> window).prefetchedTranslationData.LOGIN_INVALID_CREDENTIALS;
    this.LOGIN_NAME_TITLE = (<any> window).prefetchedTranslationData.LOGIN_NAME_TITLE;

    this.user = {
      loginId: '',
      password: ''
    };
  }

  ngAfterViewInit() {
    if(!this.ieService.isIE) {
      this.formChanged();
    }
  }

  ngOnDestroy() {
    this.loginAuthService?.destroy();
  }

  ngOnInit() {
    this.activatedRoute.data.subscribe(({ version, forgottenPasswordUrl }) => {
      this.version = version;
      this.forgottenPasswordUrl = forgottenPasswordUrl;
    });

    this.dashboardService.getClinicalUsageMessage().then((data) => {
      const nonClinicalUsage: any = data;
      this.forTestingPurposesOnly = nonClinicalUsage ? nonClinicalUsage.forTestingOnly : false;
      this.forTestingPurposesOnlyMessage = nonClinicalUsage && nonClinicalUsage.forTestingOnly ? nonClinicalUsage.loginPageMessage : '';
      this.cd.markForCheck();
    });

    const flag = sessionStorage.getItem('ShowLoggedWarning');
    const isLoggedInTab = sessionStorage.getItem('isLoggedInTab');

    if (flag === 'true' && isLoggedInTab === 'true') {
      this.coreActionCreatorService.setErrorModal(true, 'Session timeout', 'You have been logged out due to inactivity.', 'OK', () => {
        sessionStorage.setItem('ShowLoggedWarning', 'false');
        sessionStorage.removeItem('isLoggedInTab');
      });
    }

    this.loginAuthService.isLoading$.subscribe(isLoading => {
      this.loading = isLoading;
    });
  }

  formChanged() {
    this.loginForm.valueChanges
      .subscribe(data => this.onValueChanged(data));
  }

  onValueChanged(data?: IUserLogin) {
    if (!this.loginForm) {
      return;
    }

    const form = this.loginForm.form;

    for (const field in this.formErrors) {
      this.formErrors[field] = '';
      const control = form.get(field);

      if (control && control.dirty && !control.valid) {
        const messages = validationErrors[field];

        for (const key in control.errors) {
          this.formErrors[field] = messages[key] + ' ';
        }

        if (!data.loginId && !data.password) {
          this.loginErrorMessage = messages.required;
        } else if (!data.loginId) {
          this.loginErrorMessage = messages.loginId;
        }
        else {
          this.loginErrorMessage = messages.password;
        }
      }

      if (data.loginId && data.password) {
        this.loginErrorMessage = '';
      }
    }
  }

  async handleUser(response: any): Promise<void> {
    await this.loginAuthService.redirectionToDashboard(response?.body);
  }

  login(user: IUserLogin) {
    this.loading = true;
    this.loginError = false;

    // init an object that will be called on error and will hold the error data
    this.loginErrorObject = this.loginAuthService.getErrorObject(() => { // pass a callback function that will be called when finishing processing the error
      this.loginError = true;
      this.loading = false;

      if (this.loginErrorObject.redirectUri != null) {
        window.location = this.loginErrorObject.redirectUri;
      } else {
        this.cd.markForCheck();
      }
    });

    userInfo.user = { loginId: user.loginId };
    this.authService.login(user)
      .pipe(filter((data: any) => data))
      .subscribe(
        (response: ExtendedHttpResponse<any>) => {
          this.loginErrorMessage = '';
          this.handleUser(response).then();
        },
        (error: any) => {
          this.loading = false;
          this.loginErrorMessage = 'Combination of email/username and password is incorrect';
          this.loginErrorObject.errorHandler(error);
        }
      );
  }

  samlLogin = (assertion) => {
    this.loginError = false;
    this.loading = true;

    this.authService.samlLogin(assertion)
      .subscribe(
        (response) => {
          this.handleUser(response).then();
        },
        (err) => {
          if (err.status === 403 && err.data.cause === '2fa') {
            window.location = err.data.redirect_uri;
          }

          this.loginError = true;
          this.loading = false;
          this.route.navigate['/'];
        });
  }

  loadPrivacyNotes() {
    this.coreActionCreatorService.setLoader(true);
    this.workflowService.getPrivacyNotes(this.getBrowserDetectedLanguage())
      .subscribe(
        (file) => {
          this.pdfService.openPDF(file, 'Privacy Notes', undefined);
        },
        () => {
          this.notificationMessageService.error('Unable to retrieve Privacy Notes');
        },
        () => {
          this.coreActionCreatorService.setLoader(false);
        }
      );
  }

  getBrowserDetectedLanguage() {
    let languageCode = '';
    this.userActionCreatorService.getBrowserDetectedLanguage().subscribe(langCode => {
      languageCode = langCode;
    });

    return languageCode;
  }

  navigateToForgotPassword() {
    window.open(this.forgottenPasswordUrl);
  }
}
